import {useRef} from 'react';

import {Alert, SimpleGrid} from '@mantine/core';
import {I18n} from '@aws-amplify/core';
import {useNavigate} from 'react-router-dom';

import {
    DataTableTokenPagination,
    MantineDataTable,
    TableSearchForm,
} from '@flexinet/ui-components';
import {useMeteringRecords} from '../hooks/useMeteringRecords.ts';
import {useMeteringRecordsTableColumns} from '../hooks/useMeteringRecordsTableColumns.tsx';
import {moduleConfig} from '../config';
import {MeteringRecord} from '../types';

export const RecordsTable = () => {
    const {columns} = useMeteringRecordsTableColumns();
    const navigate = useNavigate();

    const {
        items,
        visibleItems,
        keyword,
        sortStatus,
        setSortStatus,
        loading,
        isError,
        isFetchingMore,
        nextToken,
        fetchRecords,
        handleFilterByKeyword,
    } = useMeteringRecords();

    const scrollViewportRef = useRef<HTMLDivElement>(null);

    const term = keyword.trim();

    return (
        <SimpleGrid verticalSpacing='xl' m='sm'>
            <TableSearchForm handleFilterByKeyword={handleFilterByKeyword}/>

            {isError && (
                <Alert color='red' title={I18n.get('Could not load metering records')}>
                    {I18n.get('The billing API did not return the metering records. Try Reload; if it persists, the API may be unavailable in this environment.')}
                </Alert>
            )}

            {/*
              NO ROW SELECTION, deliberately (FNP-534). Metering records are read-only:
              the aggregator owns their lifecycle and every billing-api writer is
              IAM-only, so there is no batch action for a checkbox to drive.

              Row click opens the detail view. MeteringRecord has a TWO-PART key:
              customerId (PK) + sortKey (SK = `meterId#periodStart#dimensionsHash`), so
              the route is two segments. BOTH MUST be encodeURIComponent'd: sortKey
              contains `#`, which would otherwise truncate the path and leave the detail
              page with a partial key. useParams() decodes each segment.
            */}
            <MantineDataTable<MeteringRecord>
                columns={columns}
                records={visibleItems}
                // Sorting is client-side over the pages loaded so far, the same scope
                // as the keyword filter. Token pagination cannot reorder unseen rows.
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
                fetching={loading}
                onScrollToBottom={() => fetchRecords()}
                scrollViewportRef={scrollViewportRef}
                withTableBorder={true}
                onRowClick={({record}) => navigate(`${moduleConfig.basePath}/${encodeURIComponent(record.customerId)}/${encodeURIComponent(record.sortKey)}`)}
            />

            {/*
              TWO EMPTY STATES (§3, §13). "No metering records yet" is normal before the
              aggregator has folded any usage; "nothing matches" means the search is too
              narrow. Gated on !loading so it does not flash during the first fetch, and
              on !isError so a failed load keeps its own Alert.
            */}
            {!loading && !isError && visibleItems.length === 0 && (
                <Alert
                    color={term ? 'gray' : 'blue'}
                    title={term ? I18n.get('No matching metering records') : I18n.get('No metering records yet')}
                >
                    {term
                        ? `${I18n.get('Nothing in the loaded metering records matches')} "${term}". ${I18n.get('Clear the search to see every loaded row. A match may still be on a page that has not been loaded yet.')}`
                        : I18n.get('Metering records are written by the usage aggregator as usage events arrive. None matches the current filter, so this is not an error.')}
                </Alert>
            )}

            <DataTableTokenPagination
                loadedCount={visibleItems.length}
                unfilteredCount={items.length}
                filterTerm={keyword}
                noun={'metering record'}
                nextToken={nextToken}
                isFetchingMore={isFetchingMore}
                handleLoadMore={fetchRecords}
            />
        </SimpleGrid>
    );
};
